// SpendDashboard — full-page AI spend tab in the AdminPanel.
//
// Reads via the public.spend_summary(p_days) RPC introduced in migration 130.
// We pull the full 90-day window once per refresh; daily buckets feed the
// hero tiles + trend chart, by_endpoint and by_user are full-90d aggregates.
//
// Why an RPC instead of pulling raw rows: PostgREST has a server-side cap
// (PGRST_DB_MAX_ROWS=1000) that the prior implementation silently hit at
// ~323 ai_usage_log rows/day. Any window >3 days undercounted by 5–15x.
// The cap is a deliberate security boundary; server-side aggregation is
// the right fix.
//
// Recent-calls table is a separate small query (50 rows; fits under cap).
//
// Harry OpenClaw ledger (Phase 1, 2026-05-15): the existing hero/trend/by-*
// view is based on ai_usage_log, which only contains harry-spend-sync's
// daily estimate of Harry's openclaw Anthropic spend (off by ~14x vs the
// actual invoice). The new harry_spend_daily_summary view (migration 146)
// is the authoritative ledger built from openclaw trajectory files. We add
// a separate "Harry OpenClaw — accurate ledger" tile row above the legacy
// hero tiles so Mo can compare side-by-side before any cutover. No data is
// removed in Phase 1; harry-spend-sync continues running unchanged.
//
// Auto-refreshes every 60s. Admin-only.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { aws } from '../lib/awsdb';
import { downloadXlsxSheets } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const SERIF = "'Tiempos', Georgia, 'Times New Roman', serif";
const MONO  = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const SUMMARY_DAYS = 90;
const RECENT_LIMIT = 50;
const REFRESH_MS = 60_000;

function fmtUsd(n, dp = 3) { return '$' + Number(n || 0).toFixed(dp); }
function fmtUsdShort(n) { const v = Number(n || 0); return v >= 10 ? '$' + v.toFixed(2) : '$' + v.toFixed(3); }
function fmtRel(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ts).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Local YYYY-MM-DD for "today" comparisons. Daily buckets from the RPC
// are stored as date_trunc('day', created_at) in DB-local time (UTC on RDS),
// so the comparison is approximate at the day boundary — close enough for
// hero tiles which exist to give a feel, not an audit-grade total.
function todayKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function monthPrefix() { return todayKey().slice(0, 7); }

export default function SpendDashboard({ session, users = [] }) {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [harryLedger, setHarryLedger] = useState([]);
  const [windowDays, setWindowDays] = useState(30);
  const [error, setError] = useState(null);
  const isAdmin = session?.role === 'admin';

  const loadData = useCallback(async () => {
    if (!isAdmin) return;
    setError(null);
    try {
      // 90-day cutoff for the harry ledger query, in YYYY-MM-DD (the view's `day` column is `date`).
      const cutoff = new Date(Date.now() - SUMMARY_DAYS * 86_400_000);
      const harryFromIso = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
      const [sumRes, recentRes, harryRes] = await Promise.all([
        aws.rpc('spend_summary', { p_days: SUMMARY_DAYS }),
        aws.from('ai_usage_log')
          .select('id, created_at, user_pin, endpoint, model, input_tokens, output_tokens, cost_usd')
          .order('created_at', { ascending: false })
          .limit(RECENT_LIMIT),
        aws.from('harry_spend_daily_summary')
          .select('day, spend_usd, turns, sessions, top_session_usd')
          .gte('day', harryFromIso)
          .order('day', { ascending: true }),
      ]);
      if (sumRes.error) throw sumRes.error;
      if (recentRes.error) throw recentRes.error;
      if (harryRes.error) throw harryRes.error;
      setSummary(sumRes.data);
      setRecent(recentRes.data || []);
      setHarryLedger(harryRes.data || []);
    } catch (e) {
      setError(e?.message || 'Failed to load spend data');
    }
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (!isAdmin) return;
    const iv = setInterval(loadData, REFRESH_MS);
    return () => clearInterval(iv);
  }, [loadData, isAdmin]);

  if (!isAdmin) return null;

  // ── Daily array (sorted ASC by day_str from the RPC) ─────────────
  const daily = useMemo(() => Array.isArray(summary?.daily) ? summary.daily : [], [summary]);

  // ── Hero windows: derived from the daily array ───────────────────
  // Today  : entry whose day matches local YYYY-MM-DD.
  // 7d     : last 7 entries (rolling-week feel; matches the trend bars).
  // MTD    : entries whose day starts with current YYYY-MM.
  // Avg/d  : 7d total / 7.
  const heroToday = useMemo(() => {
    const t = todayKey();
    const e = daily.find(d => d.day === t);
    return { cost: Number(e?.cost || 0), calls: Number(e?.calls || 0) };
  }, [daily]);

  const hero7d = useMemo(() => {
    const last7 = daily.slice(-7);
    return {
      cost:  last7.reduce((s, d) => s + Number(d.cost || 0), 0),
      calls: last7.reduce((s, d) => s + Number(d.calls || 0), 0),
    };
  }, [daily]);

  const heroMTD = useMemo(() => {
    const p = monthPrefix();
    const m = daily.filter(d => (d.day || '').startsWith(p));
    return {
      cost:  m.reduce((s, d) => s + Number(d.cost || 0), 0),
      calls: m.reduce((s, d) => s + Number(d.calls || 0), 0),
    };
  }, [daily]);

  const avgPerDay = hero7d.cost / 7;

  // ── Harry OpenClaw ledger heroes (accurate, from harry_spend_daily_summary) ──
  // Same windowing logic as the legacy heroes above but sourced from the new
  // ledger. Today/7d/MTD shapes match the legacy block so the side-by-side
  // comparison is honest. top_session_usd is unique to the ledger (per-day
  // single-session peak) and surfaces runaway sessions at a glance.
  const harryToday = useMemo(() => {
    const t = todayKey();
    const e = harryLedger.find(d => d.day === t);
    return {
      cost:       Number(e?.spend_usd || 0),
      turns:      Number(e?.turns || 0),
      sessions:   Number(e?.sessions || 0),
      topSession: Number(e?.top_session_usd || 0),
    };
  }, [harryLedger]);

  const harry7d = useMemo(() => {
    const last7 = harryLedger.slice(-7);
    return {
      cost:     last7.reduce((s, d) => s + Number(d.spend_usd || 0), 0),
      turns:    last7.reduce((s, d) => s + Number(d.turns || 0), 0),
      sessions: last7.reduce((s, d) => s + Number(d.sessions || 0), 0),
    };
  }, [harryLedger]);

  const harryMTD = useMemo(() => {
    const p = monthPrefix();
    const m = harryLedger.filter(d => (d.day || '').startsWith(p));
    return {
      cost:     m.reduce((s, d) => s + Number(d.spend_usd || 0), 0),
      turns:    m.reduce((s, d) => s + Number(d.turns || 0), 0),
      sessions: m.reduce((s, d) => s + Number(d.sessions || 0), 0),
    };
  }, [harryLedger]);

  // ── Trend: last `windowDays` entries from daily, padded to length ──
  // We pad with empty buckets so a freshly-empty 60d window still draws
  // 60 bars (so users can see "0 spend on these days" rather than thinking
  // the chart is broken).
  const trend = useMemo(() => {
    const byDay = new Map(daily.map(d => [d.day, d]));
    const out = [];
    for (let i = windowDays - 1; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86_400_000);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const key = `${y}-${m}-${dd}`;
      const entry = byDay.get(key);
      out.push({
        day: key,
        label: d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }),
        cost: Number(entry?.cost || 0),
        calls: Number(entry?.calls || 0),
      });
    }
    return out;
  }, [daily, windowDays]);
  const trendMax = useMemo(() => Math.max(0.001, ...trend.map(t => t.cost)), [trend]);
  const trendTotal = useMemo(() => trend.reduce((s, t) => s + t.cost, 0), [trend]);
  const trendCalls = useMemo(() => trend.reduce((s, t) => s + t.calls, 0), [trend]);

  // ── By endpoint / by user — RPC totals over full 90d ──────────────
  // Note: these are 90d totals regardless of windowDays selector. The
  // selector affects only the trend chart + hero (via daily slicing).
  // Per-window endpoint/user breakdowns would require either daily-bucketed
  // jsonb (~1500 rows of nested data per refresh) or an extra RPC call;
  // 90d totals are most useful for "who's burning the budget overall"
  // anyway, so we accept the trade-off.
  const byEndpoint = useMemo(() => Array.isArray(summary?.by_endpoint) ? summary.by_endpoint : [], [summary]);
  const totalEndpointCost = useMemo(() => byEndpoint.reduce((s, e) => s + Number(e.cost || 0), 0) || 1, [byEndpoint]);

  const byUser = useMemo(() => {
    const arr = Array.isArray(summary?.by_user) ? summary.by_user : [];
    return arr.map(u => {
      const usr = users.find(x => x.code === u.user_pin);
      return {
        ...u,
        cost: Number(u.cost || 0),
        calls: Number(u.calls || 0),
        name: usr?.name || (u.user_pin === 'system' ? 'System / cron' : 'Unknown'),
      };
    });
  }, [summary, users]);
  const totalUserCost = useMemo(() => byUser.reduce((s, u) => s + u.cost, 0) || 1, [byUser]);

  function userName(pin) {
    if (!pin) return 'System';
    const u = users.find(x => x.code === pin);
    return u?.name || 'Unknown';
  }

  function handleDownload() {
    const trendColumns = [
      { label: 'Day', width: 14, value: r => r.day || '' },
      { label: 'Cost (USD)', width: 14, numFmt: '"$"#,##0.000', value: r => Number(r.cost || 0) },
      { label: 'Calls', width: 10, numFmt: '#,##0', value: r => Number(r.calls || 0) },
    ];
    const endpointColumns = [
      { label: 'Endpoint', width: 26, value: r => r.endpoint || '' },
      { label: 'Cost (USD)', width: 14, numFmt: '"$"#,##0.000', value: r => Number(r.cost || 0) },
      { label: 'Calls', width: 10, numFmt: '#,##0', value: r => Number(r.calls || 0) },
    ];
    const userColumns = [
      { label: 'User', width: 22, value: r => r.name || '' },
      { label: 'Cost (USD)', width: 14, numFmt: '"$"#,##0.000', value: r => Number(r.cost || 0) },
      { label: 'Calls', width: 10, numFmt: '#,##0', value: r => Number(r.calls || 0) },
    ];
    const recentColumns = [
      { label: 'When', width: 18, value: r => r.created_at || '' },
      { label: 'User', width: 18, value: r => userName(r.user_pin) },
      { label: 'Endpoint', width: 22, value: r => r.endpoint || '' },
      { label: 'Model', width: 20, value: r => (r.model || '').replace(/^anthropic\./, '').replace(/^claude-/, '') },
      { label: 'Input Tokens', width: 14, numFmt: '#,##0', value: r => r.input_tokens ?? '' },
      { label: 'Output Tokens', width: 14, numFmt: '#,##0', value: r => r.output_tokens ?? '' },
      { label: 'Cost (USD)', width: 14, numFmt: '"$"#,##0.000', value: r => Number(r.cost_usd || 0) },
    ];
    const filename = `FON_AI_Spend_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsxSheets(filename, [
      { name: `Trend (${windowDays}d)`, columns: trendColumns, rows: trend },
      { name: 'By Endpoint (90d)', columns: endpointColumns, rows: byEndpoint },
      { name: 'By User (90d)', columns: userColumns, rows: byUser },
      { name: 'Recent Calls', columns: recentColumns, rows: recent },
    ]);
    logExportActivity(session, 'AI Spend Dashboard', filename);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* ── Harry OpenClaw — accurate ledger (from harry_spend_daily_summary, mig 146) ── */}
      <div>
        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 8 }}>
          Harry OpenClaw — accurate ledger
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            { label: 'Harry today',       value: fmtUsdShort(harryToday.cost),       sub: `${harryToday.turns} turns · ${harryToday.sessions} sessions` },
            { label: 'Harry 7 days',      value: fmtUsdShort(harry7d.cost),          sub: `${harry7d.turns} turns · ${harry7d.sessions} sessions` },
            { label: 'Harry MTD',         value: fmtUsdShort(harryMTD.cost),         sub: `${harryMTD.turns} turns · ${harryMTD.sessions} sessions` },
            { label: 'Top session today', value: fmtUsdShort(harryToday.topSession), sub: 'single-session peak' },
          ].map(t => (
            <div key={t.label} style={{ background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 12, padding: '14px 18px' }}>
              <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094' }}>{t.label}</div>
              <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 400, color: '#0F1419', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
              <div style={{ fontSize: 11, color: '#5C6166', marginTop: 2 }}>{t.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Hero tiles ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Today', value: fmtUsdShort(heroToday.cost), sub: `${heroToday.calls} calls · since midnight` },
          { label: 'Last 7 days', value: fmtUsdShort(hero7d.cost), sub: `${hero7d.calls} calls` },
          { label: 'Month to date', value: fmtUsdShort(heroMTD.cost), sub: `${heroMTD.calls} calls` },
          { label: 'Avg / day', value: fmtUsdShort(avgPerDay), sub: 'rolling 7d' },
        ].map(t => (
          <div key={t.label} style={{ background: '#FAFAF8', border: '1px solid #EFEDE7', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094' }}>{t.label}</div>
            <div style={{ fontFamily: SERIF, fontSize: 26, fontWeight: 400, color: '#0F1419', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{t.value}</div>
            <div style={{ fontSize: 11, color: '#5C6166', marginTop: 2 }}>{t.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Window selector ── */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#a1a1aa', marginRight: 4 }}>Window:</span>
        {[7, 14, 30, 60, 90].map(d => (
          <button key={d} className={`btn btn-sm ${windowDays === d ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize: 10 }} onClick={() => setWindowDays(d)}>{d}d</button>
        ))}
        <span style={{ fontSize: 11, color: '#a1a1aa', marginLeft: 'auto' }}>Total in window: {fmtUsd(trendTotal)} · {trendCalls} calls</span>
        <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>
      </div>

      {/* ── Trend chart ── */}
      <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: 16 }}>
        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 10 }}>Daily spend — last {windowDays} days</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 80 }}>
          {trend.map(t => (
            <div key={t.day} title={`${t.label}: ${fmtUsd(t.cost)} (${t.calls} calls)`} style={{
              flex: 1,
              height: `${(t.cost / trendMax) * 100}%`,
              minHeight: t.cost > 0 ? 2 : 0,
              background: t.cost > 0 ? '#036E43' : '#EFEDE7',
              borderRadius: 2,
              transition: 'height 0.2s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 9, color: '#a1a1aa', fontFamily: MONO }}>
          <span>{trend[0]?.label}</span>
          <span>{trend[Math.floor(trend.length / 2)]?.label}</span>
          <span>{trend[trend.length - 1]?.label}</span>
        </div>
      </div>

      {/* ── By endpoint + by user (two columns) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* By endpoint */}
        <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: 16 }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 12 }}>By endpoint · 90d ({byEndpoint.length})</div>
          {byEndpoint.length === 0 && <div style={{ fontSize: 12, color: '#a1a1aa', padding: 16, textAlign: 'center' }}>No calls in this window</div>}
          {byEndpoint.slice(0, 12).map(e => {
            const cost = Number(e.cost || 0);
            const calls = Number(e.calls || 0);
            const pct = (cost / totalEndpointCost) * 100;
            return (
              <div key={e.endpoint} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: '#0F1419', fontWeight: 500 }}>{e.endpoint}</span>
                  <span style={{ fontSize: 11, color: '#5C6166', fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtUsd(cost)} · {calls} calls
                  </span>
                </div>
                <div style={{ height: 4, background: '#F4F4F5', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#7c3aed', borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* By user */}
        <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: 16 }}>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 12 }}>By user · 90d ({byUser.length})</div>
          {byUser.length === 0 && <div style={{ fontSize: 12, color: '#a1a1aa', padding: 16, textAlign: 'center' }}>No calls in this window</div>}
          {byUser.map(u => {
            const pct = (u.cost / totalUserCost) * 100;
            return (
              <div key={u.user_pin} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: '#0F1419', fontWeight: 500 }}>{u.name}</span>
                  <span style={{ fontSize: 11, color: '#5C6166', fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtUsd(u.cost)} · {u.calls} calls
                  </span>
                </div>
                <div style={{ height: 4, background: '#F4F4F5', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#036E43', borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Recent calls ── */}
      <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, padding: 16 }}>
        <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8B9094', marginBottom: 12 }}>Recent calls (latest {RECENT_LIMIT})</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                {['When', 'User', 'Endpoint', 'Model', 'In', 'Out', 'Cost'].map((h, i) => (
                  <th key={h} style={{ textAlign: i >= 4 ? 'right' : 'left', padding: '6px 8px', borderBottom: '1px solid #e4e4e7', fontSize: 9, fontWeight: 600, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map(l => (
                <tr key={l.id}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', color: '#5C6166' }}>{fmtRel(l.created_at)}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', color: '#0F1419' }}>{userName(l.user_pin)}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', color: '#7c3aed', fontFamily: MONO }}>{l.endpoint}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', color: '#5C6166', fontFamily: MONO, fontSize: 10 }}>{(l.model || '').replace(/^anthropic\./, '').replace(/^claude-/, '')}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#5C6166' }}>{l.input_tokens?.toLocaleString() || '—'}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#5C6166' }}>{l.output_tokens?.toLocaleString() || '—'}</td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #fafafa', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#0F1419', fontFamily: MONO, fontWeight: 500 }}>{fmtUsd(l.cost_usd)}</td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: '#a1a1aa' }}>No calls recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {error ? (
        <div style={{ padding: '10px 12px', background: '#FBEAE5', color: '#A03422', borderRadius: 8, fontSize: 12 }}>
          {error}
        </div>
      ) : null}

      <div style={{ fontSize: 10, color: '#a1a1aa', textAlign: 'center', padding: '4px 0' }}>
        Auto-refreshes every 60s · Source: <code style={{ fontFamily: MONO }}>spend_summary({SUMMARY_DAYS}d)</code> + <code style={{ fontFamily: MONO }}>ai_usage_log</code> + <code style={{ fontFamily: MONO }}>harry_spend_daily_summary</code> (OpenClaw ledger)
      </div>
    </div>
  );
}
